import { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useCheckboxGroupOptions } from '@alltrails/denali/components/CheckboxGroup';
import { CheckboxFilterConfig } from '@alltrails/denali/components/FilterBar';
import TrailEngagement from '../../types/TrailEngagement';

export const trailEngagementValues: TrailEngagement[] = ['saved', 'not_saved', 'completed', 'not_completed'];
export const trailEngagementLabels = defineMessages<TrailEngagement>({
  saved: {
    defaultMessage: 'Saved'
  },
  not_saved: {
    defaultMessage: 'Not saved'
  },
  completed: {
    defaultMessage: 'Completed'
  },
  not_completed: {
    defaultMessage: 'Not completed'
  }
});

export const getValidTrailEngagements = (completions: string[]) =>
  completions.filter(engagement => trailEngagementValues.includes(engagement as TrailEngagement)) as TrailEngagement[];

const useTrailEngagementFilterConfig = (
  trailEngagement: TrailEngagement[] = [],
  onTrailEngagementChange: (trailEngagement: TrailEngagement[]) => void
): CheckboxFilterConfig => {
  const intl = useIntl();

  const { onChange, options, clearOptions, selectedOptions, setOptions } = useCheckboxGroupOptions(
    trailEngagementValues.map(value => ({
      id: value,
      selected: trailEngagement.includes(value),
      text: intl.formatMessage(trailEngagementLabels[value])
    }))
  );

  useEffect(() => {
    const areEqual =
      selectedOptions.length === trailEngagement.length && selectedOptions.every(option => trailEngagement.includes(option.id as TrailEngagement));
    if (!areEqual) {
      setOptions(
        trailEngagementValues.map(value => ({
          id: value,
          selected: trailEngagement.includes(value),
          text: intl.formatMessage(trailEngagementLabels[value])
        }))
      );
    }
    // stringify the array since the object itself is constantly changing.
    // only want to update when we get new values.
  }, [JSON.stringify(trailEngagement)]);

  useEffect(() => {
    onTrailEngagementChange(selectedOptions.map(option => option.id as TrailEngagement));
    // only want to trigger this the selected value changes
  }, [selectedOptions]);

  return {
    type: 'checkbox',
    id: 'trail-engagement',
    filterChip: {
      onClear: clearOptions,
      selectedItems: selectedOptions?.map(option => option.text),
      unselectedText: intl.formatMessage({ defaultMessage: 'Saved & completed' })
    },
    checkboxGroup: {
      labelText: intl.formatMessage({ defaultMessage: 'Saved & completed' }),
      onChange,
      options
    }
  };
};

export default useTrailEngagementFilterConfig;
